import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import type { OptionType } from "components/inputs";
import { MaskedInput, Select, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useCreateAdjusterMutation,
  useGetAdjustersQuery,
  usePatchAdjusterByIdMutation,
} from "services/adjuster-api";
import { api } from "services/api";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import type { AdjusterType } from "types/DataTypes";
import { useConstants } from "utils/hooks";
import { useDispatch } from "utils/redux";

type AdjusterInformationFormProps = {
  fileFromServiceId?: string;
};

const AdjusterInformationForm: React.FC<
  AdjusterInformationFormProps & { isReadOnly?: boolean }
> = ({ fileFromServiceId, isReadOnly = false }) => {
  const { constants } = useConstants();
  const { fileId: fileIdFromParams } = useParams<{ fileId: string }>();
  const dispatch = useDispatch();
  const [patchAdjusterById] = usePatchAdjusterByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [createAdjuster] = useCreateAdjusterMutation();

  const [selectedFileAdjusterId, setSelectedFileAdjusterId] = useState<
    string | null
  >(null);
  const [adjusterOptions, setAdjusterOptions] = useState<OptionType[]>([]);
  const [adjusterSearchTerm, setAdjusterSearchTerm] = useState<string | null>(
    null,
  );

  const fileId = fileFromServiceId ?? fileIdFromParams;

  const debouncedSetAdjusterSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setAdjusterSearchTerm(value);
      }, 500),
    [],
  );

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  const { data: adjusters, isFetching: isAdjustersFetching } =
    useGetAdjustersQuery(
      {
        client_id: String(fileData?.client?.id),
        searchTerm: adjusterSearchTerm,
      },
      { skip: !fileData?.client?.id || !adjusterSearchTerm },
    );

  useEffect(() => {
    if (adjusters) {
      setAdjusterOptions(
        adjusters.results?.map((adjuster: AdjusterType) => ({
          label: adjuster.name,
          value: adjuster.id.toString(),
        })) ?? [],
      );
    }
  }, [adjusters]);

  // get adjuster options from client data (like employer does)
  const adjusterOptionsFromClient = useMemo(() => {
    return (
      fileData?.client?.adjusters?.map((adjuster: AdjusterType) => ({
        label: adjuster.name,
        value: adjuster.id.toString(),
      })) ?? []
    );
  }, [fileData?.client?.adjusters]);

  // if we have a file adjuster, set it as selected
  useEffect(() => {
    if (fileData?.adjuster) {
      setSelectedFileAdjusterId(fileData.adjuster.id.toString());
    }
  }, [fileData]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      await patchAdjusterById({
        id: selectedFileAdjusterId,
        data: {
          [field]: value,
        },
      });
    },
    [patchAdjusterById, selectedFileAdjusterId],
  );

  const handleSaveFile = useCallback(
    async (field: string, value: string | number) => {
      await patchFileById({
        id: fileId,
        data: {
          [field]: value,
        },
      });
    },
    [patchFileById, fileId],
  );

  const handleCreateAndAssignAdjuster = useCallback(
    async (adjusterName: string) => {
      if (!fileId || !fileData?.client?.id) return;

      try {
        const result = await createAdjuster({
          data: {
            name: adjusterName,
            client: fileData.client.id,
            email: `${adjusterName.toLowerCase().replace(/\s+/g, ".")}@placeholder.com`,
            id: 0,
            address1: null,
            address2: null,
            city: null,
            state: null,
            zip: null,
            phone: null,
            organization: null,
            created: "",
            modified: "",
          },
        }).unwrap();

        if (result.id) {
          // invalidate adjusters cache to refresh the list
          dispatch(api.util.invalidateTags(["Adjusters"]));

          // automatically assign the newly created adjuster to the file
          await handleSaveFile("adjuster_id", result.id);

          // set as selected adjuster
          setSelectedFileAdjusterId(result.id.toString());

          // reset search state and close dropdown
          setTimeout(() => {
            setAdjusterSearchTerm(null);
            setAdjusterOptions([]);

            // remove focus from the dropdown to close it
            if (document.activeElement) {
              (document.activeElement as HTMLElement).blur();
            }
          }, 100);
        }
      } catch (err) {
        console.error("Failed to create and assign adjuster:", err);
      }
    },
    [createAdjuster, handleSaveFile, fileId, fileData?.client?.id, dispatch],
  );

  const {
    control,
    reset,
    formState: { errors },
  } = useForm();

  const selectedAdjuster = useMemo(() => {
    if (!selectedFileAdjusterId) return null;

    // first check if it's in search results
    const fromSearch = adjusters?.results?.find(
      (adjuster) => adjuster.id.toString() === selectedFileAdjusterId,
    );
    if (fromSearch) return fromSearch;

    // then check if it's in client adjusters
    const fromClient = fileData?.client?.adjusters?.find(
      (adjuster) => adjuster.id.toString() === selectedFileAdjusterId,
    );
    if (fromClient) return fromClient;

    // finally check if it's the current file adjuster
    if (fileData?.adjuster?.id.toString() === selectedFileAdjusterId) {
      return fileData.adjuster;
    }

    return null;
  }, [
    adjusters?.results,
    fileData?.client?.adjusters,
    fileData?.adjuster,
    selectedFileAdjusterId,
  ]);

  useEffect(() => {
    if (selectedAdjuster) {
      reset(selectedAdjuster);
    } else {
      // clear form when no adjuster is selected, or it's cleared
      reset({
        name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        email: "",
      });
    }
  }, [selectedAdjuster, reset]);

  return (
    <Card
      heading="Adjuster Information"
      classNames={
        fileFromServiceId ? "future-medical-adjuster-information" : ""
      }
    >
      <Select
        name="adjuster"
        label="Adjuster"
        options={
          adjusterSearchTerm ? adjusterOptions : adjusterOptionsFromClient
        }
        value={(adjusterSearchTerm
          ? adjusterOptions
          : adjusterOptionsFromClient
        ).find((a) => a.value === selectedFileAdjusterId)}
        placeholder={
          !fileData?.client?.id
            ? "Assign a client to this file to change adjuster"
            : "Search for an adjuster..."
        }
        readOnly={isReadOnly || !fileData?.client?.id}
        onChange={(o: OptionType | null) => {
          if (!isReadOnly) {
            if (o) {
              handleSaveFile("adjuster_id", o.value.toString());
              setSelectedFileAdjusterId(o.value.toString());
            } else {
              // handle clearing the adjuster
              handleSaveFile("adjuster_id", null);
              setSelectedFileAdjusterId(null);
            }
          }

          // reset search term when adjuster is cleared
          if (!o) {
            setAdjusterSearchTerm(null);
            setAdjusterOptions([]);
          }
        }}
        onInputChange={(inputValue: string) =>
          !isReadOnly && debouncedSetAdjusterSearchTerm(inputValue)
        }
        isClearable={true}
        noOptionsMessage={({ inputValue }) => {
          if (!inputValue) return "Begin typing to search for adjusters...";
          if (isAdjustersFetching) return "Searching...";

          // show "Add new item" when there's input and no results
          if (inputValue && !isAdjustersFetching) {
            return (
              <div className="custom-no-options">
                <div className="no-options-text">No adjusters found.</div>
                <button
                  type="button"
                  className="add-new-item-btn"
                  onClick={() => handleCreateAndAssignAdjuster(inputValue)}
                >
                  Add {inputValue}
                </button>
              </div>
            );
          }

          return "No adjusters found.";
        }}
      />
      <ControlledInput
        control={control}
        name="name"
        label="Adjuster Name"
        onSave={(value) => handleSave("name", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
      <ControlledInput
        control={control}
        name="address1"
        label="Adjuster Street"
        onSave={(value) => handleSave("address1", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
      <ControlledInput
        control={control}
        name="address2"
        label="APT/Suite"
        onSave={(value) => handleSave("address2", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
      <ControlledSelect
        control={control}
        label="State"
        name="state"
        options={constants?.STATES || []}
        onSave={(o: OptionType) => handleSave("state", o.value?.toString())}
        errors={errors}
        preventClear={true}
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
      <ControlledInput
        control={control}
        name="zip"
        label="Zip"
        onSave={(value) => handleSave("zip", value)}
        errors={errors}
        componentType={MaskedInput}
        mask="zipcode"
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
      <ControlledInput
        control={control}
        name="phone"
        label="Phone"
        onSave={(value) => handleSave("phone", value)}
        errors={errors}
        componentType={MaskedInput}
        mask="phone"
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
      <ControlledInput
        control={control}
        name="email"
        label="Email"
        onSave={(value) => handleSave("email", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedFileAdjusterId}
      />
    </Card>
  );
};

export default AdjusterInformationForm;
